/**
 * Pure helpers for surfacing INHERITED connections on the Connections tab.
 *
 * The unified ``/tools/connections`` endpoint (framework Phase 4) returns every connection visible
 * at the mount — own AND inherited (broader scope: network/tenant), tagged ``status``. The tab
 * already renders its OWN connections; these helpers add the INHERITED ones as read-only rows so
 * "every connection at/below the scope is visible, none unnoticed" — WITHOUT duplicating a row the
 * tab already shows for the same (kind, tool).
 *
 * Pure (no I/O) so the merge logic is unit-tested independently of the (large) tab component.
 */

/** A stable key for de-duping a connection against rows the tab already renders. */
export function connectionDedupKey(kind, toolId) {
  return `${String(kind || '').trim().toLowerCase()}:${String(toolId || '').trim()}`;
}

/**
 * Read-only row descriptors for the INHERITED connections not already shown.
 *
 * @param {Array<{kind, toolId, scope, scopeRef, label, status, configured}>} connections
 *        rows from ``listConnections`` (mixed own + inherited).
 * @param {Set<string>|string[]} shownKeys  dedup keys (see ``connectionDedupKey``) for rows the tab
 *        already renders as OWN, so an inherited copy of the same tool isn't shown twice.
 * @returns {Array<{kind:'inherited', connKind, toolId, scope, scopeRef, label, configured, rowKey}>}
 */
export function inheritedConnectionRows(connections, shownKeys) {
  const shown = shownKeys instanceof Set ? shownKeys : new Set(shownKeys || []);
  const seen = new Set();
  const out = [];
  for (const c of Array.isArray(connections) ? connections : []) {
    if (!c || c.status !== 'inherited') continue;
    const connKind = String(c.kind || '').trim();
    const toolId = String(c.toolId || '').trim();
    if (!connKind || !toolId) continue;
    const dk = connectionDedupKey(connKind, toolId);
    if (shown.has(dk)) continue;                    // tab already shows this tool (own) — skip
    const rowKey = `inherited:${connKind}:${toolId}:${String(c.scope || '')}`;
    if (seen.has(rowKey)) continue;                 // collapse duplicate inherited rows
    seen.add(rowKey);
    out.push({
      kind: 'inherited',
      connKind,
      toolId,
      scope: String(c.scope || ''),
      scopeRef: String(c.scopeRef || ''),
      label: String(c.label || toolId),
      configured: c.configured === true,
      rowKey,
    });
  }
  return out;
}
